import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './apiConfig';
import type {
  IProjectMain,
  IMasterBoardTab,
  ICmsBoardTabRow,
  IBoardUser,
  ICompany,
  IContact,
  IMarketType,
  IWorkflowTask,
  ITaskStatus,
  ITimelineRow,
  IAhuUnit,
  IMasterNameRow,
  IAhuTunnel,
  IAhuSection,
  IAllPermissions,
  IDuplicateProjectRow,
  ISalesMain,
  ISaleType,
  ISalesRep,
  ISalesPatchEcho,
  ICriticalTask,
  ICostingRow,
  IPricingRow,
  IAccountingPatchEcho,
  IShippingOrder,
  IServiceOrder,
  IProjectPdf,
  IProjectNote,
  IAhuCoilProjectRow,
  IAhuAccessDoorProjectRow,
} from 'models';

// CMS id is hardcoded to 1, version v1.0.0 — matches the Backbone source
// (PHASE3 inventory Open Question #4: single-CMS assumption, keep 1).
const CMS_ID = 1;
const CMS_VERSION = 'v1.0.0';

/**
 * RTK Query api for the Project Board (Phase 3). Reuses the shared `baseQuery`
 * (CSRF + X-Requested-With + 401→/login). Endpoints/URLs match §4 of
 * PHASE3_PROJECTBOARD_INVENTORY.md exactly (Backbone model URLs — note the
 * project detail URL carries NO trailing slash, like ProjectMainModel.url).
 *
 * NOT re-exported from the app/services barrel: several hook names would
 * collide with dashboardApi's (getCompanies/getContacts/…). Import from this
 * module directly (same convention as hooks/useDashboardData.ts).
 */
export const projectBoardApi = createApi({
  reducerPath: 'projectBoardApi',
  baseQuery,
  tagTypes: [
    'Project',
    'BoardTabs',
    'BoardTabPermissions',
    'BoardUser',
    'BoardUsers',
    'ReassignUsers',
    'BoardCompanies',
    'BoardContacts',
    'BoardMarketTypes',
    'BoardWorkflowTasks',
    'TaskStatuses',
    'Timeline',
    'AhuUnits',
    'AhuConfigurations',
    'AhuWeatherRatings',
    'AhuCmsProducts',
    'AllPermissions',
    'AhuTunnels',
    'TunnelSections',
    'SalesMain',
    'SaleTypes',
    'SalesReps',
    'CriticalTask',
    'Costings',
    'Pricings',
    'ShippingOrders',
    'ServiceOrders',
    'OrderContacts',
    'ProjectPdfs',
    'ProjectNotes',
    'ProjectCoils',
    'ProjectAccessDoors',
  ],
  endpoints: (builder) => ({
    // ---- THE project (create.model.project_main) ----
    getProject: builder.query<IProjectMain, number>({
      query: (id) => `/api/v1.0/projects/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Project', id }],
    }),

    /**
     * Per-field autosave: `PATCH /api/v1.0/projects/{id}` with ONLY the changed
     * field(s) (ProjectMainModel `save(changed|must_be_changed, {patch:true})`).
     * Optimistic cache write + server-echo merge instead of an invalidate
     * refetch — mirrors Backbone merging the PATCH response into the model
     * (and avoids a duplicate GET the original never makes).
     */
    patchProject: builder.mutation<
      Partial<IProjectMain>,
      { id: number; body: Partial<IProjectMain> }
    >({
      query: ({ id, body }) => ({
        url: `/api/v1.0/projects/${id}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
        const optimistic = dispatch(
          projectBoardApi.util.updateQueryData('getProject', id, (draft) => {
            Object.assign(draft, body);
          })
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getProject', id, (draft) => {
              Object.assign(draft, data);
            })
          );
        } catch {
          optimistic.undo();
        }
      },
    }),

    // ---- Tab permissions (fn['get.permissions']) ----
    getProjectBoardTabs: builder.query<IMasterBoardTab[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/project_board_tabs/',
      providesTags: ['BoardTabs'],
    }),
    getProjectBoardTabPermissions: builder.query<ICmsBoardTabRow[], void>({
      query: () =>
        `/api/v1.0/cms/${CMS_ID}/${CMS_VERSION}/permissions/project_board_by_role/`,
      providesTags: ['BoardTabPermissions'],
    }),

    // ---- Users ----
    getBoardUser: builder.query<IBoardUser, number>({
      query: (id) => `/api/v1.0/users/${id}`,
      providesTags: ['BoardUser'],
    }),
    getBoardUsers: builder.query<IBoardUser[], void>({
      query: () => '/api/v1.0/users/',
      providesTags: ['BoardUsers'],
    }),
    getReassignUsers: builder.query<IBoardUser[], void>({
      query: () => '/api/v1.0/users/?reassign_to=true',
      providesTags: ['ReassignUsers'],
    }),

    // ---- General Info lookup collections ----
    getBoardCompanies: builder.query<ICompany[], void>({
      query: () => '/api/v1.0/projects/companies/',
      providesTags: ['BoardCompanies'],
    }),
    getBoardContacts: builder.query<IContact[], void>({
      query: () => '/api/v1.0/projects/contacts/',
      providesTags: ['BoardContacts'],
    }),
    getBoardMarketTypes: builder.query<IMarketType[], void>({
      query: () => `/api/v1.0/cms/${CMS_ID}/${CMS_VERSION}/mfg/market_types/`,
      providesTags: ['BoardMarketTypes'],
    }),

    // ---- Status chip (shared ProjectStatusView data) ----
    getBoardWorkflowTasks: builder.query<IWorkflowTask[], void>({
      query: () => `/api/v1.0/cms/${CMS_ID}/${CMS_VERSION}/workflow/tasks/`,
      providesTags: ['BoardWorkflowTasks'],
    }),
    getTaskStatuses: builder.query<ITaskStatus[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/task_statuses/',
      providesTags: ['TaskStatuses'],
    }),
    getTimeline: builder.query<ITimelineRow[], number>({
      query: (projectId) => `/api/v1.0/projects/${projectId}/timeline/`,
      providesTags: (_r, _e, projectId) => [{ type: 'Timeline', id: projectId }],
    }),

    // ======================================================================
    // Phase 3b — embedded AHU list (#main_ahus), inventory Part B §B1.
    // ======================================================================

    /**
     * CollectionAhuUnit (B1.2): list with the EXACT legacy query string
     * `?ahu_list=true&include_versions=true&project_id={id}` (data row 127).
     */
    getAhuUnits: builder.query<IAhuUnit[], number>({
      query: (projectId) =>
        `/api/v1.0/ahu/units/?ahu_list=true&include_versions=true&project_id=${projectId}`,
      providesTags: (_r, _e, projectId) => [{ type: 'AhuUnits', id: projectId }],
    }),

    getAhuConfigurations: builder.query<IMasterNameRow[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/configurations/',
      providesTags: ['AhuConfigurations'],
    }),
    getAhuWeatherRatings: builder.query<IMasterNameRow[], void>({
      query: () => '/api/v1.0/master/v1.0.0/ahu/weather_ratings/',
      providesTags: ['AhuWeatherRatings'],
    }),
    /** CMS products with the PROJECT's cms_id — NOT hardcoded 1 (parity 128). */
    getAhuCmsProducts: builder.query<IMasterNameRow[], number>({
      query: (cmsId) => `/api/v1.0/cms/${cmsId}/${CMS_VERSION}/ahu/products/`,
      providesTags: (_r, _e, cmsId) => [{ type: 'AhuCmsProducts', id: cmsId }],
    }),
    /**
     * allPermissions (B1.2 / parity 129): the legacy stateStorage session cache
     * maps onto the RTK Query cache — fetched only on cache miss, then shared.
     * Array response; the consumer reads element [0].
     */
    getAllPermissions: builder.query<IAllPermissions[], void>({
      query: () => '/api/v1.0/users/permissions/',
      // The endpoint returns ONE object: {"permissions": {...}}. The Backbone
      // collection wraps a non-array response as a single model (toJSON()[0]),
      // so mirror that here — consumers read element [0]. Without this wrap,
      // allPermissions[0] is undefined and every AHU mutation gets locked.
      transformResponse: (response: IAllPermissions | IAllPermissions[]) =>
        Array.isArray(response) ? response : [response],
      providesTags: ['AllPermissions'],
    }),

    /**
     * ADD (B1.5 / parity 146): POST {project_id, tag} with wait semantics.
     * NO list invalidation — list refresh + auto-select arrive ONLY via the
     * socket `ahuunit`/`post` path (B1.8; OQ B-1 graceful path = legacy
     * behavior: without a socket the list silently does not refresh).
     */
    createAhu: builder.mutation<IAhuUnit, { project_id: number; tag: string }>({
      query: (body) => ({ url: '/api/v1.0/ahu/units/', method: 'POST', body }),
    }),

    /** Rename (B1.6 / parity 145): PATCH {tag}; merge echo into the cached row. */
    renameAhu: builder.mutation<
      Partial<IAhuUnit>,
      { id: number; projectId: number; tag: string }
    >({
      query: ({ id, tag }) => ({
        url: `/api/v1.0/ahu/units/${id}`,
        method: 'PATCH',
        body: { tag },
      }),
      async onQueryStarted({ id, projectId, tag }, { dispatch, queryFulfilled }) {
        const optimistic = dispatch(
          projectBoardApi.util.updateQueryData('getAhuUnits', projectId, (draft) => {
            const row = draft.find((u) => u.id === id);
            if (row) row.tag = tag;
          })
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getAhuUnits', projectId, (draft) => {
              const row = draft.find((u) => u.id === id);
              if (row) Object.assign(row, data);
            })
          );
        } catch {
          optimistic.undo();
        }
      },
    }),

    /**
     * Delete (B1.6 / parity 150): DELETE with wait semantics; the cached row is
     * removed on success (≈ the collection 'remove' listener re-render). The
     * socket delete echo is suppressed by the busy set (slice).
     */
    deleteAhu: builder.mutation<unknown, { id: number; projectId: number }>({
      query: ({ id }) => ({ url: `/api/v1.0/ahu/units/${id}`, method: 'DELETE' }),
      async onQueryStarted({ id, projectId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getAhuUnits', projectId, (draft) => {
              const i = draft.findIndex((u) => u.id === id);
              if (i !== -1) draft.splice(i, 1);
            })
          );
        } catch {
          /* wait:true — nothing removed on failure */
        }
      },
    }),

    /**
     * Duplicate (B1.6 / parity 147): POST /ahu/duplicate/unit/ JSON
     * {unit_id, project_id, tag|null}. Completion is socket-only
     * (`duplicate_status: 'success'`, B1.8).
     */
    duplicateAhu: builder.mutation<
      unknown,
      { unit_id: number; project_id: number; tag: string | null }
    >({
      query: (body) => ({ url: '/ahu/duplicate/unit/', method: 'POST', body }),
    }),

    /**
     * Create Version (B1.6 / parity 148): POST /ahu/version/create/ JSON
     * {unit_id, static, tag}. New row arrives via the socket post event.
     */
    createAhuVersion: builder.mutation<
      unknown,
      { unit_id: number; static: boolean; tag: string | null }
    >({
      query: (body) => ({ url: '/ahu/version/create/', method: 'POST', body }),
    }),

    /**
     * Convert to Main (B1.6 / parity 149): POST; the caller chains an
     * explicit units refetch + re-render (no socket dependency — the legacy
     * chains collection.fetch() then removes the preloader).
     */
    makeMainVersion: builder.mutation<unknown, { unit_id: number }>({
      query: ({ unit_id }) => ({
        url: '/ahu/version/make-main/',
        method: 'POST',
        body: { unit_id },
      }),
    }),

    /**
     * Drag-reorder mains (B1.3 / parity 151): POST /ahu/mains/reorder/
     * {project_id, ordered_ids}. Success → silent version_order set + resort of
     * the cached rows (no refetch, like the legacy); error → refetch.
     */
    reorderAhuMains: builder.mutation<
      unknown,
      { project_id: number; ordered_ids: number[] }
    >({
      query: (body) => ({ url: '/ahu/mains/reorder/', method: 'POST', body }),
      async onQueryStarted({ project_id, ordered_ids }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getAhuUnits', project_id, (draft) => {
              const pos = new Map(ordered_ids.map((id, i) => [id, i]));
              draft.forEach((u) => {
                if (pos.has(u.id)) u.version_order = pos.get(u.id) as number;
              });
              draft.sort((a, b) => {
                const ap = a.parent_version_id ?? a.id;
                const bp = b.parent_version_id ?? b.id;
                const ao = pos.has(ap) ? (pos.get(ap) as number) : Number.MAX_SAFE_INTEGER;
                const bo = pos.has(bp) ? (pos.get(bp) as number) : Number.MAX_SAFE_INTEGER;
                if (ao !== bo) return ao - bo;
                return 0;
              });
            })
          );
        } catch {
          dispatch(
            projectBoardApi.util.invalidateTags([{ type: 'AhuUnits', id: project_id }])
          );
        }
      },
    }),

    /** Drag-reorder versions within a main (B1.3): POST /ahu/versions/reorder/. */
    reorderAhuVersions: builder.mutation<
      unknown,
      { main_id: number; ordered_ids: number[]; projectId: number }
    >({
      query: ({ main_id, ordered_ids }) => ({
        url: '/ahu/versions/reorder/',
        method: 'POST',
        body: { main_id, ordered_ids },
      }),
      async onQueryStarted(
        { ordered_ids, projectId },
        { dispatch, queryFulfilled }
      ) {
        try {
          await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getAhuUnits', projectId, (draft) => {
              const pos = new Map(ordered_ids.map((id, i) => [id, i]));
              draft.forEach((u) => {
                if (pos.has(u.id)) u.version_order = pos.get(u.id) as number;
              });
            })
          );
        } catch {
          dispatch(
            projectBoardApi.util.invalidateTags([{ type: 'AhuUnits', id: projectId }])
          );
        }
      },
    }),

    // ---- Info bar sections pipeline (B1.4 / parity 142-143) ----
    getAhuTunnels: builder.query<IAhuTunnel[], number>({
      query: (ahuId) => `/api/v1.0/ahu/tunnels/?ahu_id=${ahuId}`,
      providesTags: (_r, _e, ahuId) => [{ type: 'AhuTunnels', id: ahuId }],
    }),
    getTunnelSections: builder.query<IAhuSection[], number>({
      query: (tunnelId) => `/api/v1.0/ahu/sections/?tunnel_id=${tunnelId}`,
      providesTags: (_r, _e, tunnelId) => [{ type: 'TunnelSections', id: tunnelId }],
    }),

    // ---- Duplicate popup project lists (B1.6 / parity 147) ----
    /**
     * CollectionAllProjects: unpaged `/api/v1.0/projects/` on first open;
     * server search/pagination via `?page={n}&name={q}` afterwards (the
     * component_selector projects collection `filter`).
     */
    getDuplicateProjects: builder.query<
      IDuplicateProjectRow[],
      { page?: number; name?: string } | void
    >({
      query: (args) => {
        if (!args || (args.page == null && !args.name)) return '/api/v1.0/projects/';
        return `/api/v1.0/projects/?page=${args.page ?? 1}&name=${encodeURIComponent(
          args.name ?? ''
        )}`;
      },
    }),
    /** Pinned projects — TRIPLE-N `pinnned=true` is the real param (B1.2). */
    getDuplicatePinnedProjects: builder.query<IDuplicateProjectRow[], string | void>({
      query: (name) =>
        `/api/v1.0/projects/?pinnned=true${name ? `&name=${encodeURIComponent(name)}` : ''}`,
    }),

    // ======================================================================
    // Phase 3c — Sales tab (§3.9, data rows 13–20), Timeline writes (rows
    // 24–25), Workflow popup critical task (row 26).
    // ======================================================================

    /**
     * SalesMainModel (row 13): GET /api/v1.0/projects/sales/{project_id} —
     * urlRoot + project_id, NO trailing slash (idAttribute project_id).
     */
    getSalesMain: builder.query<ISalesMain, number>({
      query: (projectId) => `/api/v1.0/projects/sales/${projectId}`,
      providesTags: (_r, _e, projectId) => [{ type: 'SalesMain', id: projectId }],
    }),

    /**
     * Sales changeInput (row 14): PATCH {field, generate_id}. The response is
     * the PROCESSED REQUEST echo (field + generate_id + set_values with the
     * server-recomputed sell_amount/ordering-rep splits) — merged into the
     * cached sales object exactly like the Backbone model merge (top-level
     * keys first, then the set_values overlay). On success the reps list is
     * invalidated = the legacy `salesRepUnitCollection.fetch()` + re-render
     * (commissions are server-recomputed — parity 71).
     */
    patchSalesMain: builder.mutation<
      ISalesPatchEcho,
      { projectId: number; body: Record<string, unknown> }
    >({
      query: ({ projectId, body }) => ({
        url: `/api/v1.0/projects/sales/${projectId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, error, { projectId }) =>
        error ? [] : [{ type: 'SalesReps', id: projectId }],
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getSalesMain', projectId, (draft) => {
              const { set_values: setValues, ...rest } = data;
              Object.assign(draft, rest);
              if (setValues) Object.assign(draft, setValues);
            })
          );
        } catch {
          /* legacy: failed save leaves the model untouched */
        }
      },
    }),

    /** SaleTypeCollection (row 15). */
    getSaleTypes: builder.query<ISaleType[], void>({
      query: () => '/api/v1.0/projects/sale_types/',
      providesTags: ['SaleTypes'],
    }),

    /** SalesRepUnitCollection (row 16). */
    getSalesReps: builder.query<ISalesRep[], number>({
      query: (projectId) => `/api/v1.0/projects/sales_reps/?sales_id=${projectId}`,
      providesTags: (_r, _e, projectId) => [{ type: 'SalesReps', id: projectId }],
    }),

    /**
     * #addRep (row 17): collection.create POST {sales_id}; success appends the
     * new row (cache push — the legacy appends a SalesRepUnitView, no refetch).
     */
    addSalesRep: builder.mutation<ISalesRep, { sales_id: number }>({
      query: (body) => ({
        url: '/api/v1.0/projects/sales_reps/',
        method: 'POST',
        body,
      }),
      async onQueryStarted({ sales_id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getSalesReps', sales_id, (draft) => {
              draft.push(data);
            })
          );
        } catch {
          /* no row appended on failure */
        }
      },
    }),

    /**
     * Rep row changeInput (row 18): PATCH {field, generate_id, sales_id};
     * the echo's `set_values` (recomputed splits) overlays the cached row for
     * display (SalesRepUnitView `unitModel`, risk 7 / parity 82).
     */
    patchSalesRep: builder.mutation<
      ISalesPatchEcho,
      { id: number; salesId: number; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({
        url: `/api/v1.0/projects/sales_reps/${id}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ id, salesId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getSalesReps', salesId, (draft) => {
              const row = draft.find((r) => r.id === id);
              if (!row) return;
              const { set_values: setValues, ...rest } = data;
              Object.assign(row, rest);
              if (setValues) Object.assign(row, setValues);
            })
          );
        } catch {
          /* row untouched on failure */
        }
      },
    }),

    /** Rep delete confirm (row 19): DELETE then remove the row. */
    deleteSalesRep: builder.mutation<unknown, { id: number; salesId: number }>({
      query: ({ id }) => ({
        url: `/api/v1.0/projects/sales_reps/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ id, salesId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getSalesReps', salesId, (draft) => {
              const i = draft.findIndex((r) => r.id === id);
              if (i !== -1) draft.splice(i, 1);
            })
          );
        } catch {
          /* model.destroy success-only removal */
        }
      },
    }),

    /**
     * Workflow popup CriticalTaskModel (row 26): returns {task_id} or {} when
     * none configured (fetch error tolerated → caller treats as 0).
     */
    getBoardCriticalTask: builder.query<ICriticalTask, void>({
      query: () => `/api/v1.0/cms/${CMS_ID}/${CMS_VERSION}/workflow/critical_tasks/`,
      providesTags: ['CriticalTask'],
    }),

    /**
     * Timeline create (row 24): first edit of a never-persisted task row POSTs
     * the full row (Backbone create on the urlRoot); also WorkflowPopup
     * create_in_collection. Response row is pushed into the timeline cache.
     */
    createTimelineRow: builder.mutation<
      ITimelineRow,
      { projectId: number; body: Partial<ITimelineRow> }
    >({
      query: ({ projectId, body }) => ({
        url: `/api/v1.0/projects/${projectId}/timeline/`,
        method: 'POST',
        body,
      }),
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getTimeline', projectId, (draft) => {
              draft.push(data);
            })
          );
        } catch {
          /* nothing added on failure */
        }
      },
    }),

    // ======================================================================
    // Phase 3d — Costing / Pricing tabs (inventory §B2/§B3, data rows 47–52).
    // ======================================================================

    /**
     * CostingCollection (row 48): UNFILTERED list — the client finds its row
     * by project_id (CostingCollection.url = urlRoot, trailing slash).
     */
    getCostings: builder.query<ICostingRow[], void>({
      query: () => '/api/v1.0/projects/costings/',
      providesTags: ['Costings'],
    }),

    /**
     * Costing field edit (row 49 / risk a — contract PINNED from
     * django/api/rest_project/viewset.py ProjectCostingViewSet +
     * costing/model/costing.js `onchange`):
     *   PATCH /api/v1.0/projects/costings/{project_id}  (NO trailing slash)
     *   body = { generate_id, change_id, <edited field> } where
     *   - generate_id = the LAST celery round-trip's id (names the report
     *     JSON `project_costing.{generate_id}.json` the server recomputes
     *     against; absent ⇒ the server short-circuits to `{}`),
     *   - change_id = model attr echo — the costing serializer NEVER returns
     *     one, so like the Backbone undefined-attr it is OMITTED when absent
     *     (JSON.stringify drops undefined keys).
     * `set` replicates the Backbone `costingActual.set({field, generate_id})`
     * that precedes save (kept on failure too — no undo, model parity). The
     * echo is merged top-level (set_values replaced wholesale), which is what
     * makes "actual prefers stored cost_net/price_raw/price_list" live.
     */
    patchCosting: builder.mutation<
      IAccountingPatchEcho,
      { projectId: number; set: Record<string, unknown>; body: Record<string, unknown> }
    >({
      query: ({ projectId, body }) => ({
        url: `/api/v1.0/projects/costings/${projectId}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ projectId, set }, { dispatch, queryFulfilled }) {
        dispatch(
          projectBoardApi.util.updateQueryData('getCostings', undefined, (draft) => {
            const row = draft.find((r) => r.project_id === projectId);
            if (row) Object.assign(row, set);
          })
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getCostings', undefined, (draft) => {
              const row = draft.find((r) => r.project_id === projectId);
              if (row) Object.assign(row, data);
            })
          );
        } catch {
          /* failed save leaves the set attrs (Backbone model parity) */
        }
      },
    }),

    /** PricingCollection (row 51): ditto costings — unfiltered list. */
    getPricings: builder.query<IPricingRow[], void>({
      query: () => '/api/v1.0/projects/pricings/',
      providesTags: ['Pricings'],
    }),

    /**
     * Pricing field edit (row 52 / risk a — PINNED from ProjectPricingViewSet
     * + pricing/model/pricing.js `onchange`):
     *   PATCH /api/v1.0/projects/pricings/{project_id}
     *   body = { generate_id, price_sched, <edited field> } where
     *   - non-empty edit: generate_id = the CURRENT celery round-trip id (the
     *     view sets it on the model right before save);
     *   - empty edit: `{field: null}` is set WITHOUT generate_id, so the PATCH
     *     carries whatever generate_id the row already holds (a previous
     *     set/echo) — possibly none on a virgin row ⇒ server returns `{}`;
     *   - price_sched is NEVER served by the GET serializer — it first
     *     arrives in a PATCH echo and is re-sent on every later PATCH
     *     (undefined ⇒ omitted, Backbone JSON parity).
     */
    patchPricing: builder.mutation<
      IAccountingPatchEcho,
      { projectId: number; set: Record<string, unknown>; body: Record<string, unknown> }
    >({
      query: ({ projectId, body }) => ({
        url: `/api/v1.0/projects/pricings/${projectId}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ projectId, set }, { dispatch, queryFulfilled }) {
        dispatch(
          projectBoardApi.util.updateQueryData('getPricings', undefined, (draft) => {
            const row = draft.find((r) => r.project_id === projectId);
            if (row) Object.assign(row, set);
          })
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getPricings', undefined, (draft) => {
              const row = draft.find((r) => r.project_id === projectId);
              if (row) Object.assign(row, data);
            })
          );
        } catch {
          /* failed save leaves the set attrs (Backbone model parity) */
        }
      },
    }),

    // ======================================================================
    // Phase 3e — Shipping / Service tabs (inventory §B6/§B7, data rows 61–70).
    // ======================================================================

    /**
     * ShippingOrderColl (row 61): GET ?project_id — server orders by -id
     * (newest first); render preserves the fetch order (parity 182).
     */
    getShippingOrders: builder.query<IShippingOrder[], number>({
      query: (projectId) =>
        `/api/v1.0/projects/shippings/?project_id=${projectId}`,
      providesTags: (_r, _e, projectId) => [{ type: 'ShippingOrders', id: projectId }],
    }),

    /**
     * `#add-order` (B6): `new ShippingOrderModel({project_id}).save({})` →
     * POST /api/v1.0/projects/shippings/ {project_id}. The created row is
     * PREPENDED to the cached list (the legacy prepends the new card's li;
     * the standalone model never joins the collection — the prepend is the
     * only list update, parity 183).
     */
    createShippingOrder: builder.mutation<IShippingOrder, { projectId: number }>({
      query: ({ projectId }) => ({
        url: '/api/v1.0/projects/shippings/',
        method: 'POST',
        body: { project_id: projectId },
      }),
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getShippingOrders', projectId, (draft) => {
              draft.unshift(data);
            })
          );
        } catch {
          /* no card prepended on failure */
        }
      },
    }),

    /**
     * Shipping per-field autosave (B6 — PINNED from ProjectShippingViewSet.
     * partial_update + shipping_order.js `onChange`):
     *   PATCH /api/v1.0/projects/shippings/{id}  (NO trailing slash)
     *   body = `this.changed` — ONLY the edited field(s); a Freight Co.
     *   change carries BOTH {freight_co_id, freight_co_contact_id: null} in
     *   ONE PATCH (single set → single change event). project_id is NOT
     *   required by the partial serializer. Response = the FULL re-serialized
     *   order, merged into the cached row (Backbone success set). `set` is
     *   applied up-front and KEPT on failure (the model keeps the local value
     *   — Backbone parity; a later reload reverts).
     */
    patchShippingOrder: builder.mutation<
      IShippingOrder,
      {
        projectId: number;
        id: number;
        set: Record<string, unknown>;
        body: Record<string, unknown>;
      }
    >({
      query: ({ id, body }) => ({
        url: `/api/v1.0/projects/shippings/${id}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ projectId, id, set }, { dispatch, queryFulfilled }) {
        dispatch(
          projectBoardApi.util.updateQueryData('getShippingOrders', projectId, (draft) => {
            const row = draft.find((r) => r.id === id);
            if (row) Object.assign(row, set);
          })
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getShippingOrders', projectId, (draft) => {
              const row = draft.find((r) => r.id === id);
              if (row) Object.assign(row, data);
            })
          );
        } catch {
          /* failed save keeps the set attrs (Backbone model parity) */
        }
      },
    }),

    /** Card delete (B6): DELETE, success-only li removal (destroy success). */
    deleteShippingOrder: builder.mutation<unknown, { projectId: number; id: number }>({
      query: ({ id }) => ({
        url: `/api/v1.0/projects/shippings/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ projectId, id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getShippingOrders', projectId, (draft) => {
              const i = draft.findIndex((r) => r.id === id);
              if (i !== -1) draft.splice(i, 1);
            })
          );
        } catch {
          /* card stays on failure */
        }
      },
    }),

    /** ServiceOrderColl (B7): GET ?project_id (ordered -id server-side). */
    getServiceOrders: builder.query<IServiceOrder[], number>({
      query: (projectId) =>
        `/api/v1.0/projects/services/?project_id=${projectId}`,
      providesTags: (_r, _e, projectId) => [{ type: 'ServiceOrders', id: projectId }],
    }),

    /** Service `#add-order`: POST {project_id}; created row prepended. */
    createServiceOrder: builder.mutation<IServiceOrder, { projectId: number }>({
      query: ({ projectId }) => ({
        url: '/api/v1.0/projects/services/',
        method: 'POST',
        body: { project_id: projectId },
      }),
      async onQueryStarted({ projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getServiceOrders', projectId, (draft) => {
              draft.unshift(data);
            })
          );
        } catch {
          /* no card prepended on failure */
        }
      },
    }),

    /**
     * Service FULL-MODEL save (B7 / parity 196 — PINNED from
     * ProjectServiceViewSet.update + service_order.js `onChange`):
     * `model.save(this.changed)` WITHOUT {patch:true} is a Backbone "update"
     * sync ⇒ PUT /api/v1.0/projects/services/{id} with the ENTIRE model
     * attribute hash (id, project_id, order_created_by, order_created_date,
     * every field — including locally-held `false`s from the invalid-number
     * quirk; NaN JSON-serializes to null). The backend REQUIRES
     * request.data['project_id'] and runs the FULL (non-partial) serializer;
     * any field-level validation failure 400s the whole save and NOTHING
     * persists (the local `set` is kept — the legacy model keeps the bad
     * value and every later full save re-sends it until fixed). Success
     * echoes the full re-serialized row (false→0.0 for FloatFields etc.),
     * merged into the cache. We do NOT replicate the legacy echo-triggered
     * cascade re-save (set(echo) → change → save again — pure write
     * amplification, 3c/3d non-replication precedent).
     */
    saveServiceOrder: builder.mutation<
      IServiceOrder,
      {
        projectId: number;
        id: number;
        set: Record<string, unknown>;
        body: Record<string, unknown>;
      }
    >({
      query: ({ id, body }) => ({
        url: `/api/v1.0/projects/services/${id}`,
        method: 'PUT',
        body,
      }),
      async onQueryStarted({ projectId, id, set }, { dispatch, queryFulfilled }) {
        dispatch(
          projectBoardApi.util.updateQueryData('getServiceOrders', projectId, (draft) => {
            const row = draft.find((r) => r.id === id);
            if (row) Object.assign(row, set);
          })
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getServiceOrders', projectId, (draft) => {
              const row = draft.find((r) => r.id === id);
              if (row) Object.assign(row, data);
            })
          );
        } catch {
          /* 400 ⇒ nothing persisted; local set kept (Backbone parity) */
        }
      },
    }),

    /** Service card delete (single confirm binding — B7 [NEG] honored). */
    deleteServiceOrder: builder.mutation<unknown, { projectId: number; id: number }>({
      query: ({ id }) => ({
        url: `/api/v1.0/projects/services/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted({ projectId, id }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getServiceOrders', projectId, (draft) => {
              const i = draft.findIndex((r) => r.id === id);
              if (i !== -1) draft.splice(i, 1);
            })
          );
        } catch {
          /* card stays on failure */
        }
      },
    }),

    /**
     * Per-company contacts (rows 64/68): GET /api/v1.0/projects/contacts/
     * ?company_id={id} — the stateStorage `'CompanyContactCollection'/{co_id}`
     * session cache (shared by Shipping AND Service) maps onto the RTK cache
     * keyed by company id; keepUnusedDataFor keeps it session-long like the
     * legacy cache (one fetch per company, parity 205).
     */
    getOrderCompanyContacts: builder.query<IContact[], number>({
      query: (companyId) => `/api/v1.0/projects/contacts/?company_id=${companyId}`,
      keepUnusedDataFor: 86400,
      providesTags: (_r, _e, companyId) => [{ type: 'OrderContacts', id: companyId }],
    }),

    // ======================================================================
    // Phase 3f — Files / Notes / Report Logo tabs (inventory §B4/§B5/§B8,
    // data rows 53–60 + 71). Server contracts PINNED from
    // django/project/views.py (KsdmProjectPDFViewSet / KsdmProjectNotesViewSet
    // + project/urls.py: detail routes have NO trailing slash) and
    // django/project/serializers.py. Both viewsets gate EVERY verb by the
    // **'project'** tab access (NOT 'files'/'notes') → 401/403 are the real
    // guards (OQ B-4 awareness; the React UI gates by the files/notes flags
    // with WORKING read-only per the inventory ruling, parity 174/175/181).
    // ======================================================================

    /** FilesCollection (row 53): GET /project/pdf/?project_id={id}. */
    getProjectPdfs: builder.query<IProjectPdf[], number>({
      query: (projectId) => `/project/pdf/?project_id=${projectId}`,
      providesTags: (_r, _e, projectId) => [{ type: 'ProjectPdfs', id: projectId }],
    }),

    /**
     * + ADD step 1 (row 54): POST /project/pdf/ {project_id, file_name:
     * 'Project file'} → 200 with the new row (id). NOT inserted into the list
     * cache here — the legacy adds the model to the collection only after the
     * follow-up multipart PATCH succeeds (addCheckFile resolve); on PATCH
     * failure the record is rolled back via DELETE (parity 169).
     */
    createPdf: builder.mutation<
      IProjectPdf,
      { project_id: number; file_name: string }
    >({
      query: (body) => ({ url: '/project/pdf/', method: 'POST', body }),
    }),

    /**
     * Upload (row 55, multipart half): raw-XHR parity — PATCH /project/pdf/{id}
     * with FormData field `pdf_file` (X-CSRFToken via the shared baseQuery;
     * fetchBaseQuery passes FormData through untouched so the browser sets the
     * multipart boundary). Server enforces: pdf-only mime + per-file cap
     * (FileValidator → 400 {'pdf_file': {'message': [...]}}) and the
     * whole-library cap (→ 400 {'message': 'Maximum library size exceeded.'}).
     * Success response = the full re-serialized row (pdf_file = served URL),
     * merged-or-pushed into the list cache (push covers the + ADD pipeline —
     * the legacy collection.add + localPdfURl iframe fallback in one step).
     */
    uploadPdfFile: builder.mutation<
      IProjectPdf,
      { id: number; projectId: number; file: File }
    >({
      query: ({ id, file }) => {
        const body = new FormData();
        body.append('pdf_file', file, file.name);
        return { url: `/project/pdf/${id}`, method: 'PATCH', body };
      },
      async onQueryStarted({ id, projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getProjectPdfs', projectId, (draft) => {
              const row = draft.find((r) => r.id === id);
              if (row) Object.assign(row, data);
              else draft.push(data);
            })
          );
        } catch {
          /* failure surfaces via the caller's error span; cache untouched */
        }
      },
    }),

    /**
     * Rename (row 55, JSON half): `model.save({file_name}, {patch:true})` —
     * NO client length guard (unlike Notes; server is authority, parity 171).
     * Echo merged into the cached row.
     */
    renamePdf: builder.mutation<
      IProjectPdf,
      { id: number; projectId: number; file_name: string }
    >({
      query: ({ id, file_name }) => ({
        url: `/project/pdf/${id}`,
        method: 'PATCH',
        body: { file_name },
      }),
      async onQueryStarted({ id, projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getProjectPdfs', projectId, (draft) => {
              const row = draft.find((r) => r.id === id);
              if (row) Object.assign(row, data);
            })
          );
        } catch {
          /* row untouched on failure */
        }
      },
    }),

    /**
     * Delete (row 56): destroy() WITHOUT wait ⇒ the legacy removes the model
     * from the collection immediately — OPTIMISTIC removal, no undo (also the
     * + ADD error-path rollback DELETE, where the row was never cached).
     */
    deletePdf: builder.mutation<unknown, { id: number; projectId: number }>({
      query: ({ id }) => ({ url: `/project/pdf/${id}`, method: 'DELETE' }),
      async onQueryStarted({ id, projectId }, { dispatch }) {
        dispatch(
          projectBoardApi.util.updateQueryData('getProjectPdfs', projectId, (draft) => {
            const i = draft.findIndex((r) => r.id === id);
            if (i !== -1) draft.splice(i, 1);
          })
        );
      },
    }),

    /** NotesCollection (row 57): GET /project/notes/?project_id={id}. */
    getProjectNotes: builder.query<IProjectNote[], number>({
      query: (projectId) => `/project/notes/?project_id=${projectId}`,
      providesTags: (_r, _e, projectId) => [{ type: 'ProjectNotes', id: projectId }],
    }),

    /**
     * Notes + ADD (row 58): POST {project_id, note_name:'Note'}; the saved
     * row joins the list (legacy collection.add + the model-sync 'change'
     * bubble re-render), then the caller selects + focuses it (parity 176).
     */
    createNote: builder.mutation<
      IProjectNote,
      { project_id: number; note_name: string }
    >({
      query: (body) => ({ url: '/project/notes/', method: 'POST', body }),
      async onQueryStarted({ project_id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getProjectNotes', project_id, (draft) => {
              draft.push(data);
            })
          );
        } catch {
          /* nothing added on failure */
        }
      },
    }),

    /**
     * Note rename / body save (row 59): PATCH {note_name} / {note_text} —
     * note_text is the RAW HTML of the contenteditable (§B5; XSS round-trip
     * replicated faithfully, parked risk). Echo merged into the cached row.
     */
    patchNote: builder.mutation<
      IProjectNote,
      { id: number; projectId: number; body: Partial<IProjectNote> }
    >({
      query: ({ id, body }) => ({
        url: `/project/notes/${id}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ id, projectId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getProjectNotes', projectId, (draft) => {
              const row = draft.find((r) => r.id === id);
              if (row) Object.assign(row, data);
            })
          );
        } catch {
          /* row untouched on failure */
        }
      },
    }),

    /** Note delete (row 60): optimistic removal, no undo (destroy parity). */
    deleteNote: builder.mutation<unknown, { id: number; projectId: number }>({
      query: ({ id }) => ({ url: `/project/notes/${id}`, method: 'DELETE' }),
      async onQueryStarted({ id, projectId }, { dispatch }) {
        dispatch(
          projectBoardApi.util.updateQueryData('getProjectNotes', projectId, (draft) => {
            const i = draft.findIndex((r) => r.id === id);
            if (i !== -1) draft.splice(i, 1);
          })
        );
      },
    }),

    /**
     * Report Logo upload (row 71): raw-XHR parity — multipart PATCH
     * /api/v1.0/projects/{id} with FormData field `report_logo` (client-gated
     * to < 3MB by the caller, parity 202). Success merges ONLY report_logo
     * into the project cache (the legacy swaps `#report_logo_img` src from the
     * response and never touches ProjectMainModel — merging the single field
     * keeps every other General-Info consumer untouched).
     */
    uploadReportLogo: builder.mutation<
      Partial<IProjectMain>,
      { id: number; file: File }
    >({
      query: ({ id, file }) => {
        const body = new FormData();
        body.append('report_logo', file, file.name);
        return { url: `/api/v1.0/projects/${id}`, method: 'PATCH', body };
      },
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getProject', id, (draft) => {
              draft.report_logo = data.report_logo;
            })
          );
        } catch {
          /* preview untouched on failure */
        }
      },
    }),

    /**
     * Timeline patch (row 25): TimelineModel autosave / WorkflowPopup saves
     * (wait:true ⇒ merge only the server echo, never optimistic).
     */
    patchTimelineRow: builder.mutation<
      ITimelineRow,
      { projectId: number; id: number; body: Partial<ITimelineRow> }
    >({
      query: ({ projectId, id, body }) => ({
        url: `/api/v1.0/projects/${projectId}/timeline/${id}`,
        method: 'PATCH',
        body,
      }),
      async onQueryStarted({ projectId, id }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            projectBoardApi.util.updateQueryData('getTimeline', projectId, (draft) => {
              const row = draft.find((r) => r.id === id);
              if (row) Object.assign(row, data);
            })
          );
        } catch {
          /* wait:true — row untouched on failure */
        }
      },
    }),

    // -----------------------------------------------------------------------
    // dev-sync F — AHU Components / AHU Accessories summary tabs (KA-5258,
    // PR #798). Two read-only project-wide list endpoints; no writes exist on
    // either tab in Backbone, so there are no matching mutations.
    // -----------------------------------------------------------------------

    /**
     * AHU Components → Coils (project mvc/ahu_components/collection/coils.js:12
     * `this.url = '/api/v1.0/ahu/coils/project/' + attr.project_id`, NO
     * trailing slash). Served by `AHUCoilProjectViewSet`
     * (api/rest_ahu/coil_viewset.py:421-443, route api/rest_ahu/urls.py:322-324):
     * a flat JSON array of every coil in the project, ordered
     * `section__tunnel__ahu__tag, id`, scoped by the same network/project
     * visibility filter every other AHU read uses.
     */
    getProjectCoils: builder.query<IAhuCoilProjectRow[], number>({
      query: (projectId) => `/api/v1.0/ahu/coils/project/${projectId}`,
      providesTags: (_r, _e, projectId) => [{ type: 'ProjectCoils', id: projectId }],
    }),

    /**
     * AHU Accessories → Access Doors (project
     * mvc/ahu_accessories/collection/access_doors.js:12
     * `'/api/v1.0/ahu/access_doors/project/' + attr.project_id`). Served by
     * `AHUAccessDoorProjectViewSet` (api/rest_ahu/access_door_viewset.py:422-442,
     * route api/rest_ahu/urls.py:540). QUIRK reproduced by doing nothing: this
     * viewset has NO `.order_by()` (unlike the coil one added alongside it), so
     * row order is whatever Postgres hands back — the AHU grouping is a stable
     * bucket-by-first-appearance, so the rendered group order (and the order of
     * rows inside a group) inherits that non-determinism, exactly as in dev.
     */
    getProjectAccessDoors: builder.query<IAhuAccessDoorProjectRow[], number>({
      query: (projectId) => `/api/v1.0/ahu/access_doors/project/${projectId}`,
      providesTags: (_r, _e, projectId) => [
        { type: 'ProjectAccessDoors', id: projectId },
      ],
    }),
  }),
});

export const {
  useGetProjectQuery,
  usePatchProjectMutation,
  useGetProjectBoardTabsQuery,
  useGetProjectBoardTabPermissionsQuery,
  useGetBoardUserQuery,
  useGetBoardUsersQuery,
  useGetReassignUsersQuery,
  useGetBoardCompaniesQuery,
  useGetBoardContactsQuery,
  useGetBoardMarketTypesQuery,
  useGetBoardWorkflowTasksQuery,
  useGetTaskStatusesQuery,
  useGetTimelineQuery,
  // Phase 3b — AHU list
  useGetAhuUnitsQuery,
  useGetAhuConfigurationsQuery,
  useGetAhuWeatherRatingsQuery,
  useGetAhuCmsProductsQuery,
  useGetAllPermissionsQuery,
  useCreateAhuMutation,
  useRenameAhuMutation,
  useDeleteAhuMutation,
  useDuplicateAhuMutation,
  useCreateAhuVersionMutation,
  useMakeMainVersionMutation,
  useReorderAhuMainsMutation,
  useReorderAhuVersionsMutation,
  useGetAhuTunnelsQuery,
  useLazyGetDuplicateProjectsQuery,
  useLazyGetDuplicatePinnedProjectsQuery,
  // Phase 3c — Sales / Timeline writes / Workflow popup
  useGetSalesMainQuery,
  usePatchSalesMainMutation,
  useGetSaleTypesQuery,
  useGetSalesRepsQuery,
  useAddSalesRepMutation,
  usePatchSalesRepMutation,
  useDeleteSalesRepMutation,
  useGetBoardCriticalTaskQuery,
  useCreateTimelineRowMutation,
  usePatchTimelineRowMutation,
  // Phase 3d — Costing / Pricing
  useGetCostingsQuery,
  usePatchCostingMutation,
  useLazyGetPricingsQuery,
  usePatchPricingMutation,
  // Phase 3e — Shipping / Service
  useGetShippingOrdersQuery,
  useCreateShippingOrderMutation,
  usePatchShippingOrderMutation,
  useDeleteShippingOrderMutation,
  useGetServiceOrdersQuery,
  useCreateServiceOrderMutation,
  useSaveServiceOrderMutation,
  useDeleteServiceOrderMutation,
  useGetOrderCompanyContactsQuery,
  // Phase 3f — Files / Notes / Report Logo
  useGetProjectPdfsQuery,
  useCreatePdfMutation,
  useUploadPdfFileMutation,
  useRenamePdfMutation,
  useDeletePdfMutation,
  useGetProjectNotesQuery,
  useCreateNoteMutation,
  usePatchNoteMutation,
  useDeleteNoteMutation,
  useUploadReportLogoMutation,
  // dev-sync F — AHU Components / AHU Accessories summary tabs
  useGetProjectCoilsQuery,
  useGetProjectAccessDoorsQuery,
} = projectBoardApi;
